import { ColumnLayout } from './ColumnLayout';
import { SectionHeader } from './SectionHeader';
import { HeroIntro } from '../_content/HeroIntro';
import { Vision } from '../_content/Vision';

/**
 * SuperHeroSection — full-width wrapper that combines:
 *   1. A shared background image (full width, no side margins)
 *   2. An animated vignette overlay (full width, no side margins)
 *   3. The HeroIntro content (title, subtitle, tagline, buttons)
 *   4. The Background Info section (image + Vision text in two columns)
 *
 * Both the background image and vignette are absolutely positioned layers
 * behind the content (z-0), while all content sits at z-10.
 */

const DevicesImage = () => (
  <img
    src="/images/content/ga-lotse-auf-allen-geraeten_links.png"
    alt="GA-Lotse auf allen Geräten"
    className="w-full h-auto object-contain"
  />
);

const SuperHeroSection = () => {
  return (
    <section id="hero" className="superhero-section bg-[#E7F0FF] pt-4" lang="de">

      {/* ── Layer 1: Background Image ─────────────────────────────────────────
          Covers the full section. bg-cover + bg-center ensures it scales
          gracefully at all viewport sizes. z-0 keeps it behind content.
      */}
      <div className="relative max-w-[1440px] mx-auto">
        <div
          className="absolute inset-0 bg-cover bg-center bg-top-right z-0 max-h-[940px] h-[960px] ml-auto max-w-[1792px] min-w-[1280px] -mt-12 -mr-44"
          style={{ backgroundImage: "url('/images/Background_SuperHero_Wide.svg')" }}
          aria-hidden="true"
          />
      </div>
    <div className="shine anim-at-start" />
    <div className="shine anim-in-intervals" />
      {/* ── Layer 2: Vignette Overlay ─────────────────────────────────────────
          Animated radial gradient. Uses .vignette class from SuperHero.css
          exactly as provided (including @property declarations + keyframes).
          z-[1] places it above the background but below content.
      */}
      <div className="vignette anim-at-start  z-[1]" aria-hidden="true" />

      {/* ── Layer 3: Content ──────────────────────────────────────────────────
          All content is constrained to the max-width container and centered.
          z-10 ensures it renders above both background layers.
      */}
      <div className="relative z-10">

        {/* ── Hero Intro Area ───────────────────────────────────────────────
            Full hero height, vertically centered, left-aligned text block.
        */}
        <div className="dpr-max-w-container h-[90vh] mx-auto px-6  flex items-center">
          <HeroIntro />
        </div>

        {/* ── Background Info Area ──────────────────────────────────────────
            Directly below the hero, inside the same wrapper (shared BG).
            Two-column layout: devices image on left, section content on right.
        */}
        <div
          id="hintergrund"
          className="dpr-max-w-container mx-auto px-0 pb-16 pt-12"
          data-aos="fade"
          data-aos-duration="900"
          data-aos-delay="200"
          data-aos-easing="ease-in-out"
        >
          <ColumnLayout
            image={<DevicesImage />}
            imageClassName="flex items-center justify-center md:-ml-8"
            className="!gap-0 self-start"
            contentClassName="px-6 md:px-0"
          >
            {/* Section header + Vision content rendered directly to avoid
                nested <section> tags (HTML5 allows it but this is cleaner) */}
            <SectionHeader
              subline="Hintergrundinformation"
              headline="Einheitliche Software für gemeinsame Ziele"
            />
            <div className="content-text font-light text-xl leading-snug">
              <Vision />
            </div>
          </ColumnLayout>
        </div>

      </div>
    </section>
  );
};

export { SuperHeroSection };
